<template>
    <Navbar 
      :showBackButton="true" 
      @back="goBack"
    />
    <div class="liquidity-ratios-container">
        <div class="header-section">
            <h2 class="page-title">📊 {{ $t('employer.liquidyRatio.title') }}</h2>
            <div class="header-actions">
                <button class="refresh-btn" @click="fetchRatios" :disabled="loading">
                    <span class="refresh-icon" :class="{ spinning: loading }">⟳</span>
                    {{ loading ? $t('employer.liquidyRatio.loading') : $t('employer.liquidyRatio.refresh') }}
                </button>
                <button class="export-btn" @click="exportData" v-if="ratiosData">
                    📥 {{ $t('employer.liquidyRatio.export') }}
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('employer.liquidyRatio.loadingRatios') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <p class="error-message">{{ error }}</p>
            <button class="retry-btn" @click="fetchRatios">{{ $t('employer.liquidyRatio.retry') }}</button>
        </div>

        <!-- Data Display -->
        <div v-else-if="ratiosData" class="ratios-content">
            <!-- Score & Summary Section -->
            <div v-if="analysisData" class="analysis-summary">
                <div class="score-card">
                    <div class="score-circle" :class="getScoreClass(analysisData.score)">
                        <span class="score-number">{{ analysisData.score || 0 }}</span>
                        <span class="score-label">امتیاز</span>
                    </div>
                    <div class="score-details">
                        <div class="score-status">{{ analysisData.executive_summary || '' }}</div>
                        <div class="score-stats">
                            <span class="stat-item critical">
                                <span class="stat-dot"></span>
                                بحرانی: {{ analysisData.alert_count_critical || 0 }}
                            </span>
                            <span class="stat-item warning">
                                <span class="stat-dot"></span>
                                هشدار: {{ analysisData.alert_count_warning || 0 }}
                            </span>
                            <span class="stat-item total">
                                <span class="stat-dot"></span>
                                کل: {{ analysisData.total_ratios || 0 }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Analysis Details -->
            <div v-if="analysisData && analysisData.analysis && analysisData.analysis.length > 0" class="analysis-section">
                <h3 class="section-title">📋 تحلیل نسبت‌ها</h3>
                <div class="analysis-grid">
                    <div 
                        v-for="item in analysisData.analysis" 
                        :key="item.ratio_name"
                        class="analysis-card"
                        :class="getAlertClass(item.alert_level)"
                    >
                        <div class="analysis-header">
                            <span class="analysis-name">{{ item.ratio_name }}</span>
                            <span class="analysis-value">{{ formatValue(item) }}</span>
                        </div>
                        <div class="analysis-body">
                            <div class="analysis-status">
                                <span class="status-badge" :class="getAlertClass(item.alert_level)">
                                    {{ item.alert_level }}
                                </span>
                                <span class="analysis-range">{{ item.standard_range }}</span>
                            </div>
                            <p class="analysis-message">{{ item.message }}</p>
                            <p class="analysis-recommendation">
                                <strong>توصیه:</strong> {{ item.recommendation }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Action Plan -->
            <div v-if="analysisData && analysisData.action_plan && analysisData.action_plan.length > 0" class="action-plan-section">
                <h3 class="section-title">📌 برنامه اقدام</h3>
                <div class="action-plan-list">
                    <div 
                        v-for="(action, index) in analysisData.action_plan" 
                        :key="index"
                        class="action-item"
                        :class="{ 'priority': index < analysisData.alert_count_critical }"
                    >
                        <span class="action-number">{{ index + 1 }}</span>
                        <span class="action-text">{{ action }}</span>
                    </div>
                </div>
            </div>

            <!-- AI Analysis Button -->
            <div ref="aiButtonSection" class="ai-analysis-section">
                <button 
                    class="ai-analysis-btn" 
                    :class="{ 
                        'has-result': aiAnalysisResult,
                        'loading': aiAnalysisLoading 
                    }"
                    style="padding: 10px 30px;"
                    @click="openAIAnalysis"
                    :disabled="aiAnalysisLoading"
                >
                    <span>🤖</span>
                    <span class="ai-btn-text">
                        {{ aiAnalysisResult ? '🔄 تحلیل مجدد با هوش مصنوعی' : 'تحلیل نسبت‌های مالی با هوش مصنوعی' }}
                    </span>
                    <span v-if="aiAnalysisLoading" class="ai-loading-spinner"></span>
                    <span v-else-if="aiAnalysisResult" class="ai-check-icon">✅</span>
                </button>
                
                <!-- Show indicator if analysis exists -->
                <div v-if="aiAnalysisResult && !showAIAnalysis" class="ai-analysis-indicator" @click="openExistingAIAnalysis">
                    <span class="indicator-dot"></span>
                    <span class="indicator-text">تحلیل هوش مصنوعی موجود است - کلیک کنید</span>
                </div>
            </div>

            <!-- AI Analysis Result -->
            <div v-if="showAIAnalysis" ref="aiResultSection" class="ai-analysis-result">
                <div class="ai-result-header">
                    <h3 class="ai-result-title">🤖 تحلیل هوش مصنوعی</h3>
                    <div class="ai-result-actions">
                        <button class="ai-refresh-btn" @click="fetchAIAnalysis" :disabled="aiAnalysisLoading" title="تولید مجدد">
                            🔄
                        </button>
                        <button class="ai-result-close" @click="closeAIAnalysis">×</button>
                    </div>
                </div>
                <div class="ai-result-body">
                    <!-- Loading -->
                    <div v-if="aiAnalysisLoading" class="ai-loading-state">
                        <div class="ai-spinner"></div>
                        <p>در حال تحلیل داده‌ها با هوش مصنوعی...</p>
                        <span class="ai-sub-text">این عملیات ممکن است چند لحظه طول بکشد</span>
                    </div>

                    <!-- Error -->
                    <div v-else-if="aiAnalysisError" class="ai-error-state">
                        <span class="ai-error-icon">⚠️</span>
                        <p>{{ aiAnalysisError }}</p>
                        <button @click="fetchAIAnalysis" class="ai-retry-btn">تلاش مجدد</button>
                    </div>

                    <!-- Success -->
                    <div v-else-if="aiAnalysisResult" class="ai-result-content">
                        <div class="ai-result-text" v-html="formatAIAnalysis(aiAnalysisResult)"></div>
                        
                        <!-- Download Button -->
                        <button class="ai-download-btn" @click="downloadAIAnalysis">
                            📥 دانلود تحلیل
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- No Data -->
        <div v-else class="empty-state">
            <div class="empty-icon">📭</div>
            <h3>{{ $t('employer.liquidyRatio.noData') }}</h3>
            <p>{{ $t('employer.liquidyRatio.noDataMessage') }}</p>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue'
import { employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue';
import { useRouter } from 'vue-router';

export default {
    name: 'LiquidityRatios',
    components: {
        Navbar,
    },
    setup() {
        const ratiosData = ref(null)
        const analysisData = ref(null)
        const loading = ref(false)
        const error = ref(null)
        const router = useRouter()
        const aiButtonSection = ref(null)
        const aiResultSection = ref(null)

        // Get company ID from localStorage or Vuex store
        const getCompanyId = () => {
            try {
                // Try to get from localStorage
                const selectedCompany = localStorage.getItem('selectedCompany')
                if (selectedCompany) {
                    const company = JSON.parse(selectedCompany)
                    return company.id || company.company_id || null
                }
                
                // Try to get from token
                const token = localStorage.getItem('token')
                if (token) {
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]))
                        return payload.company_id || payload.company_db || null
                    } catch (e) {
                        console.warn('Could not parse token:', e)
                    }
                }
                
                return null
            } catch (err) {
                console.error('Error getting company ID:', err)
                return null
            }
        }

        const companyId = computed(() => getCompanyId())

        // AI Analysis - Load from localStorage with company-specific key
        const getStorageKeys = () => {
            const cid = companyId.value || 'default'
            console.log('cid ' + cid)
            return {
                result: `ai_analysis_result_${cid}`,
                timestamp: `ai_analysis_timestamp_${cid}`,
                ratios_hash: `ai_analysis_ratios_hash_${cid}`
            }
        }

        const showAIAnalysis = ref(false)
        const aiAnalysisLoading = ref(false)
        const aiAnalysisError = ref(null)
        const aiAnalysisResult = ref(null)

        // Helper to check if ratios data has changed
        const getRatiosHash = () => {
            if (!ratiosData.value) return null
            try {
                // Create a simple hash of the ratios data
                const dataStr = JSON.stringify(ratiosData.value)
                let hash = 0
                for (let i = 0; i < dataStr.length; i++) {
                    const char = dataStr.charCodeAt(i)
                    hash = ((hash << 5) - hash) + char
                    hash = hash & hash // Convert to 32bit integer
                }
                return String(hash)
            } catch (err) {
                console.error('Error creating ratios hash:', err)
                return null
            }
        }

        // Load saved AI analysis from localStorage
        const loadSavedAIAnalysis = () => {
            try {
                const keys = getStorageKeys()
                const savedResult = localStorage.getItem(keys.result)
                const savedTimestamp = localStorage.getItem(keys.timestamp)
                const savedHash = localStorage.getItem(keys.ratios_hash)
                
                // Check if ratios have changed
                const currentHash = getRatiosHash()
                if (savedHash && currentHash && savedHash !== currentHash) {
                    console.log('Ratios data changed, clearing old AI analysis')
                    clearAIAnalysis()
                    return
                }
                
                if (savedResult) {
                    aiAnalysisResult.value = savedResult
                    
                    // Show indicator that analysis exists
                    if (savedTimestamp) {
                        const timestamp = new Date(parseInt(savedTimestamp))
                        const now = new Date()
                        const hoursDiff = (now - timestamp) / (1000 * 60 * 60)
                        
                        // If analysis is older than 24 hours, mark as stale
                        if (hoursDiff > 24) {
                            console.log('AI analysis is stale (older than 24 hours)')
                            // Optionally auto-refresh or show a warning
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading saved AI analysis:', err)
            }
        }

        // Save AI analysis to localStorage with company-specific key
        const saveAIAnalysis = (result) => {
            try {
                const keys = getStorageKeys()
                localStorage.setItem(keys.result, result)
                localStorage.setItem(keys.timestamp, String(Date.now()))
                
                // Save ratios hash to detect changes
                const hash = getRatiosHash()
                if (hash) {
                    localStorage.setItem(keys.ratios_hash, hash)
                }
            } catch (err) {
                console.error('Error saving AI analysis:', err)
            }
        }

        // Clear AI analysis from localStorage
        const clearAIAnalysis = () => {
            try {
                const keys = getStorageKeys()
                localStorage.removeItem(keys.result)
                localStorage.removeItem(keys.timestamp)
                localStorage.removeItem(keys.ratios_hash)
                aiAnalysisResult.value = null
                showAIAnalysis.value = false
            } catch (err) {
                console.error('Error clearing AI analysis:', err)
            }
        }

        // Fetch ratios from API
        const fetchRatios = async () => {
            loading.value = true
            error.value = null

            try {
                const response = await employerService.getLiquidityRatios()
                
                if (response.data.success && response.data.data) {
                    ratiosData.value = response.data.data
                    analysisData.value = {
                        analysis: response.data.analysis || [],
                        executive_summary: response.data.executive_summary || '',
                        score: response.data.score || 0,
                        alert_count_critical: response.data.alert_count_critical || 0,
                        alert_count_warning: response.data.alert_count_warning || 0,
                        total_ratios: response.data.total_ratios || 0,
                        action_plan: response.data.action_plan || []
                    }
                    console.log('Liquidity Ratios:', ratiosData.value)
                    console.log('Analysis Data:', analysisData.value)
                    
                    // Load saved analysis for this company after data is fetched
                    loadSavedAIAnalysis()
                } else {
                    error.value = response.data.message || 'خطا در دریافت داده‌ها'
                }
            } catch (err) {
                console.error('Error fetching liquidity ratios:', err)
                error.value = err.response?.data?.detail || 'خطا در ارتباط با سرور'
            } finally {
                loading.value = false
            }
        }

        // AI Analysis Methods
        const openAIAnalysis = () => {
            // Check if ratios data exists
            // if (!ratiosData.value) {
            //     aiAnalysisError.value = 'لطفاً ابتدا داده‌های نسبت‌ها را بارگذاری کنید'
            //     showAIAnalysis.value = true
            //     return
            // }
            
            // // If analysis already exists for this company, show it
            // if (aiAnalysisResult.value) {
            //     showAIAnalysis.value = true
            //     scrollToResult()
            //     return
            // }
            
            // Otherwise fetch new analysis
            showAIAnalysis.value = true
            fetchAIAnalysis()
        }

        const openExistingAIAnalysis = () => {
            // If analysis already exists for this company, show it
            if (aiAnalysisResult.value) {
                showAIAnalysis.value = true
                scrollToResult()
                return
            }
            
            // Otherwise fetch new analysis
            showAIAnalysis.value = true
            fetchAIAnalysis()
        }

        const scrollToResult = () => {
            nextTick(() => {
                if (aiResultSection.value) {
                    const yOffset = -80
                    const y = aiResultSection.value.getBoundingClientRect().top + window.pageYOffset + yOffset
                    window.scrollTo({ top: y, behavior: 'smooth' })
                }
            })
        }

        const closeAIAnalysis = () => {
            showAIAnalysis.value = false
        }

        const fetchAIAnalysis = async () => {
            // Check if ratios data exists
            if (!ratiosData.value) {
                aiAnalysisError.value = 'لطفاً ابتدا داده‌های نسبت‌ها را بارگذاری کنید'
                return
            }

            aiAnalysisLoading.value = true
            aiAnalysisError.value = null
            
            // Clear old analysis for this company
            clearAIAnalysis()

            try {
                const response = await employerService.analyzeFinancialRatios({
                    ratios: ratiosData.value,
                    analysis_type: 'comprehensive',
                    target_language: 'fa'
                })

                if (response.data.success) {
                    aiAnalysisResult.value = response.data.analysis
                    saveAIAnalysis(response.data.analysis)
                    // Scroll to result after loading
                    await nextTick()
                    scrollToResult()
                } else {
                    aiAnalysisError.value = response.data.message || 'خطا در تحلیل داده‌ها'
                }
            } catch (err) {
                console.error('Error fetching AI analysis:', err)
                aiAnalysisError.value = err.response?.data?.detail || err.message || 'خطا در ارتباط با سرور'
            } finally {
                aiAnalysisLoading.value = false
            }
        }

        const formatAIAnalysis = (text) => {
            if (!text) return ''
            // Convert markdown-like formatting to HTML
            return text
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/\n/g, '<br>')
                .replace(/•/g, '•')
                .replace(/\d+\.\s/g, '<br><strong>$&</strong>')
        }

        const downloadAIAnalysis = () => {
            if (!aiAnalysisResult.value) return
            
            const companyName = getCompanyName()
            const blob = new Blob([aiAnalysisResult.value], { type: 'text/plain;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `analysis_${companyName}_${new Date().toISOString().split('T')[0]}.txt`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        }

        const getCompanyName = () => {
            try {
                const selectedCompany = localStorage.getItem('selectedCompany')
                if (selectedCompany) {
                    const company = JSON.parse(selectedCompany)
                    return company.name || company.company_name || 'company'
                }
                return 'company'
            } catch {
                return 'company'
            }
        }

        // Format ratio numbers
        const formatRatio = (value) => {
            if (value === null || value === undefined) return '-'
            return value.toFixed(2)
        }

        // Format percentage
        const formatPercentage = (value) => {
            if (value === null || value === undefined) return '-'
            return (value * 100).toFixed(2) + '%'
        }

        // Format value based on type
        const formatValue = (item) => {
            if (item.is_percentage) {
                return (item.value * 100).toFixed(2) + '%'
            }
            return item.value.toFixed(2)
        }

        // Get class for ratio values
        const getRatioClass = (value) => {
            if (value === null || value === undefined) return ''
            if (value > 1.5) return 'good'
            if (value > 1) return 'warning'
            return 'bad'
        }

        // Get class for percentage values
        const getPercentageClass = (value) => {
            if (value === null || value === undefined) return ''
            if (value > 0.15) return 'good'
            if (value > 0) return 'warning'
            return 'bad'
        }

        // Get alert class
        const getAlertClass = (level) => {
            const map = {
                'بحرانی': 'critical',
                'هشدار': 'warning',
                'عادی': 'normal',
                'عالی': 'excellent'
            }
            return map[level] || ''
        }

        // Get score class
        const getScoreClass = (score) => {
            if (score >= 80) return 'excellent'
            if (score >= 60) return 'good'
            if (score >= 40) return 'warning'
            return 'critical'
        }

        // Get status text for ratios
        const getRatioStatus = (value) => {
            if (value === null || value === undefined) return 'نامشخص'
            if (value > 1.5) return '✅ عالی'
            if (value > 1) return '⚠️ قابل قبول'
            return '❌ نیاز به بهبود'
        }

        // Export data as JSON
        const exportData = () => {
            if (!ratiosData.value) return
            
            const data = {
                company_id: companyId.value,
                company_name: getCompanyName(),
                exported_at: new Date().toISOString(),
                ratios: ratiosData.value,
                analysis: analysisData.value,
                ai_analysis: aiAnalysisResult.value
            }
            
            const dataStr = JSON.stringify(data, null, 2)
            const blob = new Blob([dataStr], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `liquidity_ratios_${getCompanyName()}_${new Date().toISOString().split('T')[0]}.json`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        }

        const goBack = () => router.push('/employer/dashboard')

        // Lifecycle
        onMounted(() => {
            fetchRatios()
        })

        return {
            ratiosData,
            analysisData,
            loading,
            error,
            showAIAnalysis,
            aiAnalysisLoading,
            aiAnalysisError,
            aiAnalysisResult,
            aiButtonSection,
            aiResultSection,
            companyId,
            fetchRatios,
            openAIAnalysis,
            openExistingAIAnalysis,
            closeAIAnalysis,
            fetchAIAnalysis,
            formatAIAnalysis,
            downloadAIAnalysis,
            formatRatio,
            formatPercentage,
            formatValue,
            getRatioClass,
            getPercentageClass,
            getAlertClass,
            getScoreClass,
            getRatioStatus,
            exportData,
            goBack,
        }
    }
}
</script>

<style scoped>
.liquidity-ratios-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    direction: rtl;
    font-family: 'Vazir', 'Tahoma', sans-serif;
    margin-bottom: 45px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px 24px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--card-border);
}

.page-title {
    margin: 0;
    font-size: 22px;
    color: var(--heading);
}

.header-actions {
    display: flex;
    gap: 10px;
}

.refresh-btn,
.export-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
}

.refresh-btn {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
}

.refresh-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.export-btn {
    background: var(--primary);
    color: white;
}

.export-btn:hover {
    background: var(--primary-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--ring);
}

.refresh-icon.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 60px 20px;
}

.spinner {
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

/* Error State */
.error-state {
    text-align: center;
    padding: 40px 20px;
    background: #fff3cd;
    border-radius: 12px;
    border: 1px solid #ffc107;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 12px;
}

.error-message {
    color: #856404;
    margin-bottom: 16px;
}

.retry-btn {
    padding: 10px 24px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s ease;
}

.retry-btn:hover {
    background: var(--primary-hover);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px dashed var(--border);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state h3 {
    margin: 0 0 8px;
    color: var(--heading);
}

.empty-state p {
    color: var(--text-muted);
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--card-border);
}

.summary-icon {
    font-size: 32px;
}

.summary-info {
    display: flex;
    flex-direction: column;
}

.summary-label {
    font-size: 13px;
    color: var(--text-muted);
}

.summary-value {
    font-size: 20px;
    font-weight: 700;
}

.summary-value.good {
    color: #22c55e;
}

.summary-value.warning {
    color: #f59e0b;
}

.summary-value.bad {
    color: #ef4444;
}

.summary-value.positive {
    color: #22c55e;
}

.summary-value.negative {
    color: #ef4444;
}

/* Ratios Grid */
.ratios-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.ratio-section {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid var(--card-border);
}

.ratio-section.full-width {
    grid-column: 1 / -1;
}

.section-title {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--heading);
    border-bottom: 2px solid var(--border);
    padding-bottom: 10px;
}

/* Ratio Card */
.ratio-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ratio-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: var(--bg);
    border-radius: 8px;
    border: 1px solid var(--border);
}

.ratio-label {
    font-size: 14px;
    color: var(--text);
    font-weight: 500;
}

.ratio-value {
    font-size: 16px;
    font-weight: 600;
}

.ratio-value.good {
    color: #22c55e;
}

.ratio-value.warning {
    color: #f59e0b;
}

.ratio-value.bad {
    color: #ef4444;
}

.ratio-status {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 12px;
    background: var(--card-border);
}

/* Balance Grid */
.balance-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

.balance-item {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    background: var(--bg);
    border-radius: 8px;
    border: 1px solid var(--border);
}

.balance-label {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 4px;
}

.balance-value {
    font-size: 18px;
    font-weight: 600;
}

.balance-value.positive {
    color: #22c55e;
}

.balance-value.negative {
    color: #ef4444;
}

/* PNL Grid */
.pnl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

.pnl-item {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    background: var(--bg);
    border-radius: 8px;
    border: 1px solid var(--border);
}

.pnl-label {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 4px;
}

.pnl-value {
    font-size: 18px;
    font-weight: 600;
}

.pnl-value.positive {
    color: #22c55e;
}

.pnl-value.negative {
    color: #ef4444;
}

.pnl-value.good {
    color: #22c55e;
}

.pnl-value.warning {
    color: #f59e0b;
}

.pnl-value.bad {
    color: #ef4444;
}

/* Responsive */
@media (max-width: 992px) {
    .ratios-grid {
        grid-template-columns: 1fr;
    }
    
    .ratio-section.full-width {
        grid-column: 1;
    }
}

@media (max-width: 768px) {
    .liquidity-ratios-container {
        padding: 12px;
    }
    
    .header-section {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }
    
    .page-title {
        font-size: 18px;
        text-align: center;
    }
    
    .header-actions {
        width: 100%;
        justify-content: center;
    }
    
    .summary-cards {
        grid-template-columns: 1fr 1fr;
    }
    
    .summary-card {
        padding: 14px;
    }
    
    .summary-value {
        font-size: 16px;
    }
    
    .balance-grid,
    .pnl-grid {
        grid-template-columns: 1fr;
    }
    
    .ratio-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
}

@media (max-width: 480px) {
    .summary-cards {
        grid-template-columns: 1fr;
    }
    
    .ratio-value {
        font-size: 14px;
    }
    
    .balance-value {
        font-size: 16px;
    }
    
    .pnl-value {
        font-size: 16px;
    }
}

/* Dark mode support */
[data-theme="dark"] .error-state {
    background: #1c1917;
    border-color: #f59e0b;
}

[data-theme="dark"] .error-message {
    color: #fbbf24;
}

[data-theme="dark"] .ratio-item {
    background: #1a1a2e;
}

[data-theme="dark"] .balance-item {
    background: #1a1a2e;
}

[data-theme="dark"] .pnl-item {
    background: #1a1a2e;
}
.analysis-summary {
    margin-bottom: 24px;
}

.score-card {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 20px 24px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px solid var(--card-border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.score-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
}

.score-circle.excellent {
    background: #22c55e;
    color: white;
}

.score-circle.good {
    background: #3b82f6;
    color: white;
}

.score-circle.warning {
    background: #f59e0b;
    color: white;
}

.score-circle.critical {
    background: #ef4444;
    color: white;
}

.score-number {
    font-size: 28px;
    font-weight: 700;
}

.score-label {
    font-size: 12px;
    opacity: 0.9;
}

.score-details {
    flex: 1;
}

.score-status {
    font-size: 16px;
    font-weight: 600;
    color: var(--heading);
    margin-bottom: 8px;
}

.score-stats {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--text-muted);
}

.stat-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.stat-item.critical .stat-dot {
    background: #ef4444;
}

.stat-item.warning .stat-dot {
    background: #f59e0b;
}

.stat-item.total .stat-dot {
    background: #3b82f6;
}

/* Analysis Section */
.analysis-section {
    margin-bottom: 24px;
}

.analysis-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
}

.analysis-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid var(--card-border);
    transition: all 0.3s ease;
}

.analysis-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.analysis-card.critical {
    border-right: 4px solid #ef4444;
}

.analysis-card.warning {
    border-right: 4px solid #f59e0b;
}

.analysis-card.normal {
    border-right: 4px solid #3b82f6;
}

.analysis-card.excellent {
    border-right: 4px solid #22c55e;
}

.analysis-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.analysis-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--heading);
}

.analysis-value {
    font-size: 16px;
    font-weight: 700;
}

.analysis-card.critical .analysis-value {
    color: #ef4444;
}

.analysis-card.warning .analysis-value {
    color: #f59e0b;
}

.analysis-card.normal .analysis-value {
    color: #3b82f6;
}

.analysis-card.excellent .analysis-value {
    color: #22c55e;
}

.analysis-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.analysis-status {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-badge {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
}

.status-badge.critical {
    background: #fef2f2;
    color: #dc2626;
}

.status-badge.warning {
    background: #fffbeb;
    color: #d97706;
}

.status-badge.normal {
    background: #eff6ff;
    color: #2563eb;
}

.status-badge.excellent {
    background: #ecfdf5;
    color: #059669;
}

.analysis-range {
    font-size: 12px;
    color: var(--text-muted);
}

.analysis-message {
    font-size: 13px;
    color: var(--text);
    margin: 4px 0;
}

.analysis-recommendation {
    font-size: 13px;
    color: var(--text);
    padding: 8px 12px;
    background: var(--bg);
    border-radius: 6px;
    margin: 4px 0 0;
}

/* Action Plan */
.action-plan-section {
    margin-bottom: 24px;
}

.action-plan-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: var(--card-bg);
    border-radius: 8px;
    border: 1px solid var(--card-border);
    transition: all 0.3s ease;
}

.action-item:hover {
    background: var(--bg);
}

.action-item.priority {
    border-right: 3px solid #ef4444;
    background: #fef2f2;
}

.action-number {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-muted);
    min-width: 24px;
}

.action-text {
    font-size: 14px;
    color: var(--text);
    line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
    .score-card {
        flex-direction: column;
        text-align: center;
        padding: 16px;
    }
    
    .score-stats {
        justify-content: center;
    }
    
    .analysis-grid {
        grid-template-columns: 1fr;
    }
    
    .action-item {
        padding: 10px 12px;
    }
}

/* Dark mode support */
[data-theme="dark"] .analysis-card.critical {
    background: #1c1917;
    border-color: #f87171;
}

[data-theme="dark"] .analysis-card.warning {
    background: #1c1917;
    border-color: #fbbf24;
}

[data-theme="dark"] .analysis-card.normal {
    background: #1c1917;
    border-color: #60a5fa;
}

[data-theme="dark"] .analysis-card.excellent {
    background: #1c1917;
    border-color: #34d399;
}

[data-theme="dark"] .status-badge.critical {
    background: #1c1917;
    color: #fca5a5;
}

[data-theme="dark"] .status-badge.warning {
    background: #1c1917;
    color: #fcd34d;
}

[data-theme="dark"] .action-item.priority {
    background: #1c1917;
    border-color: #f87171;
}
/* AI Analysis Button */
.ai-analysis-section {
    margin: 24px 0;
    text-align: center;
}

.ai-analysis-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.5);
}

.ai-analysis-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.ai-icon {
    font-size: 24px;
}

.ai-loading-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
}

/* AI Analysis Result */
.ai-analysis-result {
    margin-top: 24px;
    background: var(--card-bg);
    border-radius: 16px;
    border: 2px solid #8b5cf6;
    box-shadow: 0 4px 24px rgba(139, 92, 246, 0.15);
    overflow: hidden;
    animation: fadeIn 0.3s ease-out;
}

.ai-result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.ai-result-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.ai-result-close {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 0 8px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.ai-result-close:hover {
    opacity: 1;
}

.ai-result-body {
    padding: 24px;
    max-height: 500px;
    overflow-y: auto;
}

/* AI Loading State */
.ai-loading-state {
    text-align: center;
    padding: 40px 20px;
}

.ai-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid var(--border);
    border-top: 4px solid #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

.ai-sub-text {
    display: block;
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 8px;
}

/* AI Error State */
.ai-error-state {
    text-align: center;
    padding: 40px 20px;
}

.ai-error-icon {
    font-size: 48px;
    display: block;
    margin-bottom: 12px;
}

.ai-retry-btn {
    padding: 8px 24px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    margin-top: 12px;
}

/* AI Result Content */
.ai-result-text {
    font-size: 15px;
    line-height: 1.8;
    color: var(--text);
    white-space: pre-wrap;
}

.ai-result-text strong {
    color: var(--heading);
}

.ai-result-text em {
    color: var(--primary);
}

.ai-download-btn {
    margin-top: 16px;
    padding: 10px 20px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    color: var(--text);
}

.ai-download-btn:hover {
    background: var(--hover);
    border-color: var(--primary);
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
    .ai-analysis-btn {
        width: 100%;
        justify-content: center;
        padding: 12px 20px;
        font-size: 14px;
    }

    .ai-result-body {
        padding: 16px;
        max-height: 400px;
    }

    .ai-result-text {
        font-size: 14px;
    }
}

@media (max-width: 480px) {
    .ai-analysis-btn {
        font-size: 13px;
        padding: 10px 16px;
    }

    .ai-icon {
        font-size: 17px;
    }
}

/* Dark Mode Support */
[data-theme="dark"] .ai-analysis-result {
    border-color: #8b5cf6;
    box-shadow: 0 4px 24px rgba(139, 92, 246, 0.2);
}

[data-theme="dark"] .ai-analysis-btn {
    background: linear-gradient(135deg, #4a3a8a 0%, #5a3a8a 100%);
}

.ai-analysis-section {
    margin: 28px 0 16px;
    text-align: center;
    position: relative;
}

.ai-analysis-btn {
    position: relative;
    padding: 3px 10px;
    background-color: var(--bg);
    color: var(--text);
    border: none;
    border-radius: 16px;
    font-size: 17px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
    min-width: 280px;
    justify-content: center;
    letter-spacing: 0.5px;
    border: 1px solid #764ba2;
}



.ai-analysis-btn:hover:not(:disabled)::before {
    opacity: 1;
}

.ai-analysis-btn:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.5);
}

.ai-analysis-btn:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
}

.ai-analysis-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

.ai-analysis-btn.has-result {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
}

.ai-analysis-btn.has-result:hover:not(:disabled) {
    box-shadow: 0 8px 32px rgba(34, 197, 94, 0.5);
}

.ai-analysis-btn.loading {
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    box-shadow: 0 4px 20px rgba(107, 114, 128, 0.4);
}

.ai-btn-text {
    font-size: 16px;
}

.ai-check-icon {
    font-size: 20px;
}

.ai-loading-spinner {
    width: 22px;
    height: 22px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
    flex-shrink: 0;
}

/* Analysis Indicator */
.ai-analysis-indicator {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    margin-top: 10px;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #16a34a;
    font-size: 13px;
}

.ai-analysis-indicator:hover {
    background: rgba(34, 197, 94, 0.15);
    transform: translateY(-1px);
}

.indicator-dot {
    width: 8px;
    height: 8px;
    background: #22c55e;
    border-radius: 50%;
    animation: pulse-dot 2s ease-in-out infinite;
}

.indicator-text {
    font-weight: 500;
}

@keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.2); }
}

/* AI Analysis Result */
.ai-analysis-result {
    margin-top: 20px;
    background: var(--card-bg);
    border-radius: 16px;
    border: 2px solid #8b5cf6;
    box-shadow: 0 4px 24px rgba(139, 92, 246, 0.15);
    overflow: hidden;
    animation: fadeIn 0.4s ease-out;
}

.ai-result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.ai-result-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.ai-result-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ai-refresh-btn {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: white;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-refresh-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    transform: rotate(45deg);
}

.ai-refresh-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.ai-result-close {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: white;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-result-close:hover {
    background: rgba(255, 255, 255, 0.25);
}

.ai-result-body {
    padding: 24px;
    max-height: 600px;
    overflow-y: auto;
}

/* AI Loading State */
.ai-loading-state {
    text-align: center;
    padding: 40px 20px;
}

.ai-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid var(--border);
    border-top: 4px solid #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

.ai-sub-text {
    display: block;
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 8px;
}

/* AI Error State */
.ai-error-state {
    text-align: center;
    padding: 40px 20px;
}

.ai-error-icon {
    font-size: 48px;
    display: block;
    margin-bottom: 12px;
}

.ai-retry-btn {
    padding: 10px 28px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    margin-top: 12px;
    transition: all 0.3s ease;
}

.ai-retry-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

/* AI Result Content */
.ai-result-content {
    position: relative;
}

.ai-result-text {
    font-size: 15px;
    line-height: 2;
    color: var(--text);
    white-space: pre-wrap;
    padding: 4px 0;
    text-align: right;
}

.ai-result-text strong {
    color: var(--heading);
    font-weight: 700;
}

.ai-result-text em {
    color: var(--primary);
    font-style: italic;
}

.ai-result-text br {
    display: block;
    margin: 4px 0;
}

.ai-download-btn {
    margin-top: 20px;
    padding: 12px 24px;
    background: var(--bg);
    border: 2px solid var(--border);
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    color: var(--text);
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.ai-download-btn:hover {
    background: var(--hover);
    border-color: var(--primary);
    transform: translateY(-2px);
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {


    .ai-btn-text {
        font-size: 14px;
    }

    .ai-result-body {
        padding: 16px;
        max-height: 400px;
    }

    .ai-result-text {
        font-size: 14px;
        line-height: 1.8;
    }

    .ai-analysis-indicator {
        font-size: 12px;
        padding: 6px 12px;
    }
}

@media (max-width: 480px) {
    .ai-analysis-btn {
        font-size: 13px;
        padding: 3px 10px;
        border-radius: 12px;
        gap: 8px;
    }

    .ai-btn-text {
        font-size: 12px;
    }

    .ai-result-text {
        font-size: 13px;
    }

    .ai-download-btn {
        width: 100%;
        justify-content: center;
    }
}

/* Dark Mode Support */
[data-theme="dark"] .ai-analysis-result {
    border-color: #8b5cf6;
    box-shadow: 0 4px 24px rgba(139, 92, 246, 0.2);
}


[data-theme="dark"] .ai-analysis-indicator {
    background: rgba(34, 197, 94, 0.08);
}
</style>